import { Box } from '@chakra-ui/react';
import React, { useState } from 'react';

import { type ContextMenuListSubMenu } from './types';
import { type RenderMenuState } from './render-menu';

/**
 * Simple chevron right icon component (unicode arrow)
 * Used for submenu indicators without requiring @chakra-ui/icons
 */
const ChevronRight = (): React.ReactElement => (
	<Box as="span" fontSize="12px" ml={2}>&#9656;</Box>
);

type SubMenuProps = {
	subMenuItem: ContextMenuListSubMenu;
	state: RenderMenuState;
	index: number;
	renderMenuFn: (params: { menuList: any[]; state: RenderMenuState }) => React.ReactElement[];
};

/**
 * SubMenu component that renders a flyout submenu on hover
 * Uses pure CSS/React state for hover behavior to work within the parent context menu
 */
export const SubMenu = ({ subMenuItem, state, index, renderMenuFn }: SubMenuProps): React.ReactElement => {
	const [isHovered, setIsHovered] = useState(false);

	const isDisabled = !(
		typeof subMenuItem.isEnabled === 'undefined' ||
		(typeof subMenuItem.isEnabled === 'boolean' && subMenuItem.isEnabled) ||
		(typeof subMenuItem.isEnabled === 'function' && subMenuItem.isEnabled({ controller: state.controller }))
	);

	return (
		<Box
			key={index}
			position="relative"
			onMouseEnter={() => !isDisabled && setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Box
				w="100%"
				px={3}
				py={1.5}
				cursor={isDisabled ? 'not-allowed' : 'pointer'}
				opacity={isDisabled ? 0.4 : 1}
				bg={isHovered && !isDisabled ? 'gray.100' : 'transparent'}
				display="flex"
				alignItems="center"
				justifyContent="space-between"
				borderRadius="md"
				fontSize="sm"
				{...subMenuItem.itemProps}
			>
				<Box as="span">{subMenuItem.label}</Box>
				<ChevronRight />
			</Box>

			{isHovered && !isDisabled && (
				<Box
					position="absolute"
					left="100%"
					top="0"
					minW="150px"
					bg="white"
					boxShadow="md"
					borderRadius="md"
					border="1px solid"
					borderColor="gray.200"
					zIndex={1500}
					py={1}
				>
					{renderMenuFn({ menuList: subMenuItem.items, state })}
				</Box>
			)}
		</Box>
	);
};

/**
 * Renders a nested submenu as a flyout menu that appears on hover
 */
export const renderSubMenu = (
	subMenuItem: ContextMenuListSubMenu,
	state: RenderMenuState,
	index: number,
	renderMenuFn: (params: { menuList: any[]; state: RenderMenuState }) => React.ReactElement[]
): React.ReactElement => {
	return <SubMenu key={index} subMenuItem={subMenuItem} state={state} index={index} renderMenuFn={renderMenuFn} />;
};
